/**
 * Country data for the phone number picker.
 * Ordered so Indonesia (home market) + top Bali visitor source countries appear first.
 * `code` is ISO 3166-1 alpha-2 (used to render the flag emoji).
 * `dial` is the international dialing code (without the leading +).
 */
export const COUNTRIES = [
  // ─── Home + top source markets for Bali ────────────────────────
  { name: "Indonesia", code: "ID", dial: "62" },
  { name: "Australia", code: "AU", dial: "61" },
  { name: "Singapore", code: "SG", dial: "65" },
  { name: "Malaysia", code: "MY", dial: "60" },
  { name: "United States", code: "US", dial: "1" },
  { name: "United Kingdom", code: "GB", dial: "44" },
  { name: "China", code: "CN", dial: "86" },
  { name: "Japan", code: "JP", dial: "81" },
  { name: "South Korea", code: "KR", dial: "82" },
  { name: "India", code: "IN", dial: "91" },
  { name: "Netherlands", code: "NL", dial: "31" },
  { name: "Germany", code: "DE", dial: "49" },
  { name: "France", code: "FR", dial: "33" },
  { name: "Russia", code: "RU", dial: "7" },
  { name: "Thailand", code: "TH", dial: "66" },
  { name: "Philippines", code: "PH", dial: "63" },
  { name: "Vietnam", code: "VN", dial: "84" },
  { name: "Hong Kong", code: "HK", dial: "852" },
  { name: "Taiwan", code: "TW", dial: "886" },
  { name: "New Zealand", code: "NZ", dial: "64" },
  // ─── Rest of the world (alphabetical) ──────────────────────────
  { name: "Argentina", code: "AR", dial: "54" },
  { name: "Austria", code: "AT", dial: "43" },
  { name: "Bahrain", code: "BH", dial: "973" },
  { name: "Bangladesh", code: "BD", dial: "880" },
  { name: "Belgium", code: "BE", dial: "32" },
  { name: "Brazil", code: "BR", dial: "55" },
  { name: "Brunei", code: "BN", dial: "673" },
  { name: "Cambodia", code: "KH", dial: "855" },
  { name: "Canada", code: "CA", dial: "1" },
  { name: "Chile", code: "CL", dial: "56" },
  { name: "Colombia", code: "CO", dial: "57" },
  { name: "Croatia", code: "HR", dial: "385" },
  { name: "Czechia", code: "CZ", dial: "420" },
  { name: "Denmark", code: "DK", dial: "45" },
  { name: "Egypt", code: "EG", dial: "20" },
  { name: "Estonia", code: "EE", dial: "372" },
  { name: "Finland", code: "FI", dial: "358" },
  { name: "Greece", code: "GR", dial: "30" },
  { name: "Hungary", code: "HU", dial: "36" },
  { name: "Iceland", code: "IS", dial: "354" },
  { name: "Iran", code: "IR", dial: "98" },
  { name: "Iraq", code: "IQ", dial: "964" },
  { name: "Ireland", code: "IE", dial: "353" },
  { name: "Israel", code: "IL", dial: "972" },
  { name: "Italy", code: "IT", dial: "39" },
  { name: "Jordan", code: "JO", dial: "962" },
  { name: "Kazakhstan", code: "KZ", dial: "7" },
  { name: "Kenya", code: "KE", dial: "254" },
  { name: "Kuwait", code: "KW", dial: "965" },
  { name: "Laos", code: "LA", dial: "856" },
  { name: "Latvia", code: "LV", dial: "371" },
  { name: "Lebanon", code: "LB", dial: "961" },
  { name: "Lithuania", code: "LT", dial: "370" },
  { name: "Luxembourg", code: "LU", dial: "352" },
  { name: "Macau", code: "MO", dial: "853" },
  { name: "Maldives", code: "MV", dial: "960" },
  { name: "Mexico", code: "MX", dial: "52" },
  { name: "Mongolia", code: "MN", dial: "976" },
  { name: "Morocco", code: "MA", dial: "212" },
  { name: "Myanmar", code: "MM", dial: "95" },
  { name: "Nepal", code: "NP", dial: "977" },
  { name: "Nigeria", code: "NG", dial: "234" },
  { name: "Norway", code: "NO", dial: "47" },
  { name: "Oman", code: "OM", dial: "968" },
  { name: "Pakistan", code: "PK", dial: "92" },
  { name: "Papua New Guinea", code: "PG", dial: "675" },
  { name: "Peru", code: "PE", dial: "51" },
  { name: "Poland", code: "PL", dial: "48" },
  { name: "Portugal", code: "PT", dial: "351" },
  { name: "Qatar", code: "QA", dial: "974" },
  { name: "Romania", code: "RO", dial: "40" },
  { name: "Saudi Arabia", code: "SA", dial: "966" },
  { name: "Serbia", code: "RS", dial: "381" },
  { name: "Slovakia", code: "SK", dial: "421" },
  { name: "Slovenia", code: "SI", dial: "386" },
  { name: "South Africa", code: "ZA", dial: "27" },
  { name: "Spain", code: "ES", dial: "34" },
  { name: "Sri Lanka", code: "LK", dial: "94" },
  { name: "Sweden", code: "SE", dial: "46" },
  { name: "Switzerland", code: "CH", dial: "41" },
  { name: "Türkiye", code: "TR", dial: "90" },
  { name: "Ukraine", code: "UA", dial: "380" },
  { name: "United Arab Emirates", code: "AE", dial: "971" },
  { name: "Uruguay", code: "UY", dial: "598" },
  { name: "Uzbekistan", code: "UZ", dial: "998" },
  { name: "Venezuela", code: "VE", dial: "58" },
  { name: "Yemen", code: "YE", dial: "967" },
];

/** Convert ISO-2 country code → flag emoji (renders on iOS/Android/macOS; Windows shows letters). */
export const codeToFlag = (code = "") =>
  code
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

/** Given a full E.164 number like "+6281234", return the best-matching country (longest dial-code match). */
export const parseCountryFromNumber = (value = "", fallbackCode = "ID") => {
  if (typeof value === "string" && value.startsWith("+")) {
    const digits = value.slice(1);
    // longest prefix first (some dials are 1–4 digits)
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    const hit = sorted.find((c) => digits.startsWith(c.dial));
    if (hit) return hit;
  }
  return COUNTRIES.find((c) => c.code === fallbackCode) || COUNTRIES[0];
};
